/* eslint-disable max-lines-per-function */
import React from "react";
import moment from "moment";
import { Typography, Grid } from "@mui/material";
import { ApplicantSubs_submissions as ClientSubmissions,
  ApplicantSubmissions_clientInterests as ClientInterests,
  ApplicantSubs_submissions_form_property_interestList as ClientInterestsSub,
  ApplicantSubmissions_clientInterests_interestList_property as ClientProperty,
  Feature } from "types/gql-types";
import { InterestStatus } from "model/interest/status";

import { humanizeFormType } from "utils/form-types";
import { Button, LinkButton } from "components/haven-design";
import { usePropertyFeature } from "components/feature";
import RemoveUserModal from "./remove-user-modal";
import RemoveDraftModal from "./remove-draft-modal";
import styles from "./interests.module.css";
import ReinstateModalLoader from "./reinstate-modal-loader";


const getApplicationType = (data: ClientInterests | ClientSubmissions) => {
  if ("form" in data) {
    const { form: { type: formType } } = data;
    return humanizeFormType(formType);
  }
  return "";
};

const formatDate = (submittedAt: number) =>
  moment(new Date(submittedAt * 1000)).format("MMMM D, YYYY");

const updateFormURL = (data: ClientInterests | ClientSubmissions) => {
  // If interest:
  if ("updateRequest" in data) {
    const request = data.updateRequest;
    if (request && request.isActive) return `/update/${request.code}`;

    if (data.interestList === null) return null;
    return `${data.interestList.id}/${data.id}`;
  }
  // If submission:
  return `/application/${data.id}`;
};

const PropertyInfo: React.FC<{
  submittedAt: number | null,
  property: ClientProperty,
  data: ClientInterests | ClientSubmissions
}> = ({ data, property, submittedAt }) => {
  const phone = property?.phone || "N/A";
  const email = property?.email || "support@havenconnect.com";
  const propertyName = property?.name || "Property";
  const mailToLink = `mailto:${email}?subject=${propertyName} Question`;


  return (<>
    <div>
      <Typography variant="h6">{propertyName} {getApplicationType(data)}</Typography>
      {submittedAt !== null && (
        <Typography variant="body2" color="textSecondary" component="span">
          Applied on: {formatDate(submittedAt)}
        </Typography>
      )}
    </div>
    <div>
      <span className="fa fa-envelope" style={{ margin: "6px 6px 0 0" }} />
      <a href={mailToLink}>{email}</a>
    </div>
    <div>
      <span className="fa fa-phone" style={{ margin: "6px 6px 0 0" }} />
      <a href={`tel:${phone}`}>{phone}</a>
    </div>
  </>);
};


const ApplicationStatus: React.FC<{
  status: string;
  data: ClientInterests | ClientSubmissions;
  isArchived: boolean;
}> = ({ status, data, isArchived }) =>
  <div>
    <div>
      <span className={styles.statusLabel}>Status:</span>
      <span className={styles.statusValue}>{status}</span>
    </div>
    {status === InterestStatus.Active &&
    !isArchived && ("status" in data) && data.status && (
      <div>
        <span className={styles.statusLabel}>Stage:</span>
        <span className={styles.statusValue}>{data.status.name}</span>
      </div>
    )}
  </div>;


type ApplicationProps = {
  status: string,
  statusChangedAt: string,
  type: "application" | "submission",
  submittedAt: number | null,
  interestList: ClientInterests["interestList"] | ClientInterestsSub,
  data: ClientInterests | ClientSubmissions,
  onApplicationRemoved: () => void
  property: ClientProperty,
}

const ReinstateButton: React.FC<ApplicationProps & {onClick: () => void}> = ({
  property: { id: propertyId },
  onClick,
  status,
  interestList: { isArchived },
}) => {
  const reinstatmentEnabled = usePropertyFeature(propertyId, Feature.REQUEST_REINSTATEMENT);
  if (status !== InterestStatus.Removed) return null;
  if (isArchived) return null;

  if (!reinstatmentEnabled) return null;

  return <div style={{ width: "fit-content", margin: "0 auto" }}>
    <Button color="secondary" size="L" onClick={onClick}>
      Request Reinstatement
    </Button>
  </div>;
};
const UpdateButton: React.FC<ApplicationProps> = ({
  data,
  status,
  type,
  interestList: { isArchived },
}) => {
  const updateURL = updateFormURL(data);
  const isFormEditable = [InterestStatus.Active, "Draft"].includes(status) && !isArchived;

  return <div style={{ width: "fit-content", margin: "0 auto" }}>
    {updateURL !== null && isFormEditable && (
      <LinkButton to={updateURL} color="primary" size="L">
        {type === "application" ? "Click to Update info" : "Continue"}
      </LinkButton>
    )}
  </div>;
};

const Application: React.FC<ApplicationProps> = (props) => {
  const {
    data,
    property, interestList: { isArchived, name },
    type,
    status,
    statusChangedAt,
    onApplicationRemoved,
    submittedAt,
  } = props;
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [isReinstateModalOpen, setReinstateModalOpen] = React.useState(false);
  const [isDraftModalOpen, setDraftModalOpen] = React.useState(false);
  const handleOnRequestClose = (isRemoved: boolean) => {
    setModalOpen(false);
    setDraftModalOpen(false);
    setReinstateModalOpen(false);
    if (isRemoved) onApplicationRemoved();
  };
  if (props.interestList === null) return null;
  return (
    <Grid size={12}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PropertyInfo
            data={data}
            property={property}
            submittedAt={submittedAt}
          />
          <div className={styles.remove}>
            {status === InterestStatus.Active && !isArchived && type === "application" && (
              <a onClick={() => setModalOpen(true)} role="button">Remove me from this list</a>
            )}
            {type === "submission" && (<a onClick={() => setDraftModalOpen(true)} role="button">Remove draft</a>)}
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <ApplicationStatus status={status} data={data} isArchived={!!isArchived} />
        </Grid>
        <Grid size={{ xs: 12, md: 3, lg: 4 }}>
          <UpdateButton {...props} />
          <ReinstateButton {...props} onClick={() => setReinstateModalOpen(true)}/>
        </Grid>
      </Grid>
      <hr />
      <ReinstateModalLoader
        isOpen={isReinstateModalOpen}
        onRequestClose={handleOnRequestClose}
        interestListName={name || ""}
        removalDate={statusChangedAt}
        reinstatementDays={props.property.reinstatementDays}
        interestId={props.data.id}
      />
      <RemoveUserModal
        isOpen={isModalOpen}
        onRequestClose={handleOnRequestClose}
        interestListName={name || ""}
        interestId={props.data.id}
      />
      <RemoveDraftModal
        isOpen={isDraftModalOpen}
        onRequestClose={handleOnRequestClose}
        submissionId={props.data.id}
        interestListName={name || ""}
        formType={getApplicationType(props.data)}
      />
    </Grid>
  );
};

export default Application;
